import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { useWizard } from '../../Contexts/WizardContext';
import Calendar from './CalendarComponents/Calendar';
import { getAcuityAvailability } from '../../api/endpoints';
import ServiceSidebar from './CalendarComponents/ServiceSidebar';
import { ScheduleConfirmModal } from './CalendarComponents/ScheduleConfirmModal';
import { Button } from '../General/Button';
import { CodeSquare, MessageCircleWarning } from 'lucide-react';
import { formatDateToUSA } from '../../config/utils';

const categoryTitleMap = {
    lawn_services: <>Select <span className='text-green-800'>Lawn Services</span> Appointment Time</>,
    irrigation_services: <>Select <span className='text-blue-800'>Irrigation Services</span> Appointment Time</>,
    yard_cleanup:  <>Select <span className='text-yellow-800'>Yard Cleanups</span> Appointment Time</>,
    power_rake:  <>Select <span className='text-indigo-800'>Power Rake</span> Appointment Time</>,
  };

export const CalendarView = () => {
  const {
    updateFormData,
    nextStep,
    previousStep,
    formData,
    calendarSubStep,
    nextCalendarSubStep,
    previousCalendarSubStep,
  } = useWizard();

  const requiredCalendars = formData.services
  const currentCategoryId = requiredCalendars[calendarSubStep - 1].id;
  const currentServiceName = requiredCalendars[calendarSubStep - 1].name
  const nearestDateAvailable = formatDateToUSA(format(requiredCalendars[calendarSubStep - 1].min_date, 'yyyy-MM-dd'))
  const totalCalendars = requiredCalendars.length;
  const isLastCalendar = calendarSubStep >= totalCalendars;

  const title = categoryTitleMap[currentCategoryId] || 'Select Appointment Time';

  const allInstancesInBucket = useMemo(() => {
    return (formData.serviceInstances || []).filter(
      inst => inst.schedulingBucketId === currentCategoryId
    );
  }, [formData.serviceInstances, currentCategoryId]);
  
  const pendingInstancesInBucket = useMemo(() => {
    return allInstancesInBucket.filter(inst => inst.status === 'pending');
  }, [allInstancesInBucket]);

  const [scheduledAlready, setScheduledAlready] = useState(!pendingInstancesInBucket.length)

  useEffect(() => {
    setScheduledAlready(pendingInstancesInBucket.length === 0)
  }, [pendingInstancesInBucket])

  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedTime, setSelectedTime] = useState(null)
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [appointmentNotes, setAppointmentNotes] = useState('')
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);

  const [isDataLoading, setIsDataLoading] = useState(false)

  const [showConfirmModal, setShowConfirmModal] = useState(false);
    
  useEffect(() => {
    async function fetch() {
      setIsDataLoading(true)
      const area = formData.jobsiteData.service_area.service_area
      const acuityAvailability = await getAcuityAvailability({
        service_area: area,
        service_type: currentCategoryId,
        requested_month: currentMonth.getMonth(),
        requested_year: currentMonth.getFullYear()
      })
      setAvailableSlots(acuityAvailability)
      setIsDataLoading(false)
    }
    fetch()
  }, [currentMonth, currentCategoryId])


  useEffect(() => {
    if (selectedTime) {
      setShowConfirmModal(true)
    }
  }, [selectedTime])


  // -------------- Handlers ----------------

  const handleContinue = () => {
    if (isLastCalendar) {
      nextStep();
    } else {
      nextCalendarSubStep();
    }
  };

  const handleBack = () => {
    if (calendarSubStep > 1) {
      previousCalendarSubStep();
    } else {
      previousStep();
    }
  };

  const handleModalConfirm = (selectedInstanceIds) => {
    if (!selectedDate || !selectedTime || !selectedSlot) return;
    
    const selectedInstancesFullData = allInstancesInBucket.filter(item => selectedInstanceIds.includes(item.instanceId))
    const otherInstancesCurrentBacket = allInstancesInBucket.filter(item => !selectedInstanceIds.includes(item.instanceId))
    const instancesOtherBuckets = formData.serviceInstances.filter(inst => inst.schedulingBucketId != currentCategoryId)
    
    const newAppointment = {
      categoryId: currentCategoryId,
      serviceName: currentServiceName,
      appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
      service_type: currentCategoryId,
      appointmentTime: selectedTime,
      selectedSlot: selectedSlot,
      services: selectedInstancesFullData,
      appointmentNotes: appointmentNotes,
      app_price: selectedInstancesFullData.map(el => el.price).reduce((a,b) => a+b,0)
    }
    updateFormData(oldVal => {
      const existingAppointments = oldVal?.appointments || []
      const instancesSelected = selectedInstancesFullData.map(item => {
        return {
          ...item,
          appointmentDate: format(selectedDate, 'yyyy-MM-dd'),
          appointmentTime: selectedTime,
          selectedSlot: selectedSlot,
          status: 'scheduled'
        }
      })
      const filteredAppointments = existingAppointments.filter(a => a.categoryId !== currentCategoryId);
      const existingAppointmentsForCategory = existingAppointments.filter(a => a.categoryId == currentCategoryId); 
      
      return {
        ...oldVal,
        appointments:  [...filteredAppointments, ...existingAppointmentsForCategory, newAppointment],
        serviceInstances: [...instancesOtherBuckets, ...otherInstancesCurrentBacket, ...instancesSelected]
      }
    })

    setShowConfirmModal(false);
    resetSectedStates()
    const stillPending = otherInstancesCurrentBacket.filter(el => el.status == 'pending')
    if (stillPending.length > 0) {
      // no action as there are still services to be scheduled current calendar
    } else if (!isLastCalendar) {
      nextCalendarSubStep();
    } else {
      nextStep();
    }
  };

  const resetSectedStates = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedSlot(null);
    setAppointmentNotes('')
    setCurrentMonth(new Date())
  }

  const resetAllServicesSelection = () => {
    const instancesOtherBuckets = formData.serviceInstances.filter(inst => inst.schedulingBucketId != currentCategoryId)
    const instancesCurrentBuckets = formData.serviceInstances.filter(inst => inst.schedulingBucketId == currentCategoryId)
    const resetInstances = instancesCurrentBuckets.map(({ appointmentDate, appointmentTime, ...rest }) => ({
      ...rest,
      status: 'pending'
    }))

    updateFormData(oldVal => {
      const existingAppointments = oldVal?.appointments || []
      const filteredAppointments = existingAppointments.filter(
        a => a.categoryId !== currentCategoryId
      );      
      
      return {
        ...oldVal,
        appointments:  filteredAppointments,
        serviceInstances: [...instancesOtherBuckets, ...resetInstances]
      }
    })
    // setAllInstancesInBuket([...instancesOtherBuckets, ...resetInstances])
  }

  // -------- Templates -------------

  const warningBlockTemplate = () => {
    return (
      <div className="flex items-start gap-4 rounded-xl border border-amber-200 bg-amber-50 p-5">
        <div className="mt-0.5 text-amber-500">
          <MessageCircleWarning/>
        </div>
        <div className="flex flex-col gap-3 items-center">
          <p className="text-sm font-medium text-amber-900">
            You've scheduled this service already — do you want to change your selection?
          </p>
          <button
            onClick={resetAllServicesSelection}
            className="rounded-lg bg-amber-900 px-4 py-2 text-sm font-medium text-white hover:bg-amber-800 transition-colors">
              Reset Selection
          </button>
        </div>
      </div>
    )
  }
  
  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="text-center mb-6">
        {totalCalendars > 1 && (
          <div className="inline-block bg-green-100 border border-green-300 rounded-full px-5 py-2 mb-3">
            <p className="text-base sm:text-lg font-bold text-green-800">
              Calendar {calendarSubStep} of {totalCalendars}: {currentServiceName}
            </p>
          </div>
        )}
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">
          {title}
        </h2>
        <p className="text-gray-600">Select a date and time to schedule your services. Nearest date available: 
          <span> </span>
          <span 
            className='cursor-pointer underline font-bold text-green-600 hover:text-green-800 transition-colors duration-150'
            onClick={() => setCurrentMonth(new Date(nearestDateAvailable))}
          >{nearestDateAvailable}</span>
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="lg:w-72 flex-shrink-0">
          <ServiceSidebar
            instances={allInstancesInBucket}
            currentBucketLabel={currentServiceName}
            onAddService={() => {
              console.log('on add service click')
              // setShowAddServiceModal(true)
            }}
          />
        </div>
            {scheduledAlready ? 
              warningBlockTemplate()
            : 
              <Calendar
                currentMonth={currentMonth}
                setCurrentMonth={setCurrentMonth}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                availableSlots={availableSlots}
                isLoading={isDataLoading}
                setSelectedSlot={setSelectedSlot}
              />
            }

      </div>

      {selectedDate && selectedTime && (
        <ScheduleConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false)
            resetSectedStates()
          }}
          onConfirm={handleModalConfirm}
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          pendingInstances={pendingInstancesInBucket}
          setAppointmentNotes={setAppointmentNotes}
        />
      )}
      <div className="flex gap-4 mt-6">
          <Button
            label='Back'
            isDisabled={false}
            onClick={handleBack}
          />
          <Button
            label='Continue'
            isDisabled={allInstancesInBucket.find(el => el.status == 'pending')}
            onClick={handleContinue}
            primary
          />
        </div>
    </div>
  );
};
